import { motion } from 'framer-motion'
import { ThumbsUp } from 'lucide-react'
import * as React from 'react'
import { createPortal } from 'react-dom'

import { cn } from '@/lib/utils'

import NextImage from '@/components/NextImage'
import { ReactionListDialog } from '@/components/reactions/reaction-list-dialog'
import { EmojiPicker } from '@/components/ui/emoji-picker'

import { MessageEmojiData, MessageReaction, MessageReactionType } from '@/types/chat.type'
import { UserProfile } from '@/types/user.type'

interface MessageReactionsProps {
  messageId: number
  reactions: MessageReaction[]
  onReact: (messageId: number, reactionType: MessageReactionType, emojiData?: MessageEmojiData) => void
  currentUser?: UserProfile
  classNameReactionText?: string
  isOwnMessage?: boolean
  /** Show only the reaction button without the reacted icons */
  buttonOnly?: boolean
  /** Show only the reacted icons without the reaction button */
  iconsOnly?: boolean
}

// Default 5 quick reactions
const defaultReactions: Record<
  string,
  {
    activeColor: string
    hoverBgColor: string
    hoverShadow: string
    activeBgColor: string
    hoverColor: string
    emoji: string
    name: string
  }
> = {
  like: {
    activeColor: 'text-blue-600',
    hoverBgColor: '#eff6ff',
    hoverShadow: '0 0 12px rgba(37, 99, 235, 0.6)',
    activeBgColor: '#dbeafe',
    hoverColor: '#2563eb',
    emoji: '👍',
    name: 'Like',
  },
  love: {
    activeColor: 'text-pink-600',
    hoverBgColor: '#fdf2f8',
    hoverShadow: '0 0 12px rgba(219, 39, 119, 0.6)',
    activeBgColor: '#fce7f3',
    hoverColor: '#db2777',
    emoji: '❤️',
    name: 'Love',
  },
  haha: {
    activeColor: 'text-yellow-600',
    hoverBgColor: '#fefce8',
    hoverShadow: '0 0 12px rgba(217, 119, 6, 0.6)',
    hoverColor: '#ca8a04',
    activeBgColor: '#fef9c3',
    emoji: '😂',
    name: 'Haha',
  },
  wow: {
    activeColor: 'text-yellow-500',
    hoverBgColor: '#fffbeb',
    hoverShadow: '0 0 12px rgba(245, 158, 11, 0.6)',
    activeBgColor: '#fef3c7',
    hoverColor: '#eab308',
    emoji: '😮',
    name: 'Wow',
  },
  sad: {
    activeColor: 'text-purple-600',
    hoverBgColor: '#faf5ff',
    hoverShadow: '0 0 12px rgba(147, 51, 234, 0.6)',
    activeBgColor: '#f3e8ff',
    hoverColor: '#9333ea',
    emoji: '😢',
    name: 'Sad',
  },
}

function useFixedQuickReactionBarStyle(
  visible: boolean,
  anchorRef: React.RefObject<HTMLDivElement | null>,
  isOwnMessage: boolean,
  gapPx: number
) {
  const [style, setStyle] = React.useState<React.CSSProperties | null>(null)

  React.useLayoutEffect(() => {
    if (!visible) {
      setStyle(null)
      return
    }

    const update = () => {
      const el = anchorRef.current
      if (!el) return
      const rect = el.getBoundingClientRect()
      setStyle({
        position: 'fixed',
        zIndex: 10050,
        bottom: window.innerHeight - rect.top + gapPx,
        ...(isOwnMessage
          ? { right: window.innerWidth - rect.right, left: 'auto' }
          : { left: rect.left, right: 'auto' }),
      })
    }

    update()
    window.addEventListener('scroll', update, true)
    window.addEventListener('resize', update)
    return () => {
      window.removeEventListener('scroll', update, true)
      window.removeEventListener('resize', update)
    }
  }, [visible, anchorRef, isOwnMessage, gapPx])

  return style
}

function MessageReactions({
  messageId,
  reactions,
  onReact,
  currentUser,
  classNameReactionText: _classNameReactionText,
  isOwnMessage = false,
  buttonOnly = false,
  iconsOnly = false,
}: MessageReactionsProps) {
  const [showReactionBar, setShowReactionBar] = React.useState(false)
  const [animatingReaction, setAnimatingReaction] = React.useState<string | null>(null)
  const [showReactionList, setShowReactionList] = React.useState(false)
  const [isEmojiPickerOpen, setIsEmojiPickerOpen] = React.useState(false)

  const reactionBarRef = React.useRef<HTMLDivElement>(null)
  const hideTimeoutRef = React.useRef<NodeJS.Timeout | null>(null)

  const groupedReactions = React.useMemo(() => {
    const groups = new Map<
      string,
      {
        identifier: string
        reactionType: string
        emojiData?: MessageEmojiData
        count: number
        users: Array<{ id: number; name: string }>
        userReacted: boolean
        isDefault: boolean
      }
    >()

    reactions.forEach(reaction => {
      // Create unique identifier for grouping
      let identifier: string
      let isDefault = false

      if (defaultReactions[reaction.reactionType]) {
        // It's a default reaction
        identifier = reaction.reactionType
        isDefault = true
      } else if (reaction.emojiData?.unicode) {
        // It's a custom emoji
        identifier = reaction.emojiData.unicode
      } else if (reaction.emojiData?.imageUrl) {
        // It's a custom meme
        identifier = reaction.emojiData.imageUrl
      } else {
        // Fallback
        identifier = reaction.reactionType
      }

      const existing = groups.get(identifier)

      if (existing) {
        existing.count++
        existing.users.push({
          id: reaction.user.id,
          name: `${reaction.user.firstName} ${reaction.user.lastName}`,
        })
        if (reaction.user.id === currentUser?.id) {
          existing.userReacted = true
        }
      } else {
        groups.set(identifier, {
          identifier,
          reactionType: reaction.reactionType,
          emojiData: reaction.emojiData,
          count: 1,
          users: [
            {
              id: reaction.user.id,
              name: `${reaction.user.firstName} ${reaction.user.lastName}`,
            },
          ],
          userReacted: reaction.user.id === currentUser?.id,
          isDefault,
        })
      }
    })

    return Array.from(groups.values()).sort((a, b) => b.count - a.count)
  }, [reactions, currentUser])

  // Get user's custom emoji reactions for the emoji picker
  const userCustomReactions = React.useMemo(() => {
    return reactions
      .filter(reaction => reaction.user.id === currentUser?.id && reaction.emojiData)
      .map(reaction => ({
        unicode: reaction.emojiData?.unicode,
        imageUrl: reaction.emojiData?.imageUrl,
        name: reaction.emojiData?.name,
      }))
  }, [reactions, currentUser])

  const handleDefaultReactionClick = (reactionType: string) => {
    setAnimatingReaction(reactionType)
    onReact(messageId, reactionType as MessageReactionType)
    setShowReactionBar(false)

    // Clear animation after a short delay
    setTimeout(() => setAnimatingReaction(null), 600)
  }

  const handleCustomEmojiSelect = (emoji: string) => {
    const emojiData: MessageEmojiData = {
      unicode: emoji,
      shortcode: `:${emoji}:`,
      name: emoji,
    }
    onReact(messageId, 'custom', emojiData)
    setShowReactionBar(false)
  }

  const handleMemeEmojiSelect = (memeData: { id: number; name: string; imageUrl: string }) => {
    const emojiData: MessageEmojiData = {
      imageUrl: memeData.imageUrl,
      name: memeData.name,
      shortcode: `:${memeData.name}:`,
    }
    onReact(messageId, 'custom', emojiData)
    setShowReactionBar(false)
  }

  const handleMouseEnter = () => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current)
      hideTimeoutRef.current = null
    }
    setShowReactionBar(true)
  }

  const handleMouseLeave = () => {
    // Don't hide the reaction bar if emoji picker is open
    if (isEmojiPickerOpen) {
      return
    }

    // Add a small delay before hiding to allow users to move between elements
    hideTimeoutRef.current = setTimeout(() => {
      setShowReactionBar(false)
    }, 300) // 300ms delay
  }

  // Clean up timeout on unmount or when emoji picker state changes
  React.useEffect(() => {
    return () => {
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current)
      }
    }
  }, [])

  // Clear timeout when emoji picker opens
  React.useEffect(() => {
    if (isEmojiPickerOpen && hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current)
      hideTimeoutRef.current = null
    }
  }, [isEmojiPickerOpen])

  const quickReactionFloatStyle = useFixedQuickReactionBarStyle(showReactionBar, reactionBarRef, isOwnMessage, 8)

  const renderReactionDisplay = (
    group: {
      identifier: string
      reactionType: string
      emojiData?: MessageEmojiData
      count: number
      users: Array<{ id: number; name: string }>
      userReacted: boolean
      isDefault: boolean
    },
    size: 'small' | 'normal' = 'normal'
  ) => {
    const isSmall = size === 'small'
    const sizeClasses = isSmall ? 'w-4 h-4 text-xs' : 'w-5 h-5 text-sm'

    if (group.isDefault && defaultReactions[group.reactionType]) {
      const config = defaultReactions[group.reactionType]
      return <span className={cn('flex items-center justify-center', sizeClasses)}>{config.emoji}</span>
    }

    if (group.emojiData?.imageUrl) {
      return (
        <NextImage
          src={group.emojiData.imageUrl}
          alt={group.emojiData.name || 'Custom reaction'}
          className={cn('object-contain rounded', sizeClasses)}
          width={isSmall ? 16 : 20}
          height={isSmall ? 16 : 20}
          unoptimized
        />
      )
    }

    if (group.emojiData?.unicode) {
      // Check if unicode contains HTML (from old data)
      if (group.emojiData.unicode.includes('<img')) {
        // Extract image URL from HTML string
        const imgMatch = group.emojiData.unicode.match(/src="([^"]+)"/)
        if (imgMatch) {
          return (
            <NextImage
              src={imgMatch[1]}
              alt={group.emojiData.name || 'Custom reaction'}
              className={cn('object-contain rounded', sizeClasses)}
              width={isSmall ? 16 : 20}
              height={isSmall ? 16 : 20}
              unoptimized
            />
          )
        }
      }
      return <span className={cn('flex items-center justify-center', sizeClasses)}>{group.emojiData.unicode}</span>
    }

    // Fallback
    return <span className={cn('flex items-center justify-center', sizeClasses)}>{group.reactionType}</span>
  }

  const getAnimationVariant = (reactionType: string) => {
    if (animatingReaction === reactionType) {
      return {
        scale: [1, 1.3, 1],
        rotate: [0, 10, -10, 0],
        transition: { duration: 0.6 },
      }
    }
    return {}
  }

  const renderQuickReactionBarBody = () => (
    <>
      {Object.entries(defaultReactions).map(([type, config]) => {
        const userHasThisReaction = groupedReactions.some(
          group => group.reactionType === type && group.userReacted
        )

        return (
          <motion.button
            key={type}
            onClick={() => handleDefaultReactionClick(type)}
            className="flex items-center justify-center w-6 h-6 rounded-full mx-0.5 transition-colors text-sm"
            style={{
              backgroundColor: userHasThisReaction ? config.activeBgColor : 'transparent',
            }}
            title={config.name}
            whileHover={{
              scale: 1.25,
              backgroundColor: config.hoverBgColor,
              boxShadow: config.hoverShadow,
              transition: { duration: 0.2 },
            }}
            whileTap={{ scale: 0.9 }}
            animate={getAnimationVariant(type)}
          >
            <span className="text-sm">{config.emoji}</span>
          </motion.button>
        )
      })}

      <div className="w-px h-4 bg-gray-200 mx-1" />

      <EmojiPicker
        onEmojiSelect={handleCustomEmojiSelect}
        onCustomEmojiSelect={handleMemeEmojiSelect}
        onRemoveReaction={emojiData => onReact(messageId, 'custom', emojiData)}
        onOpenChange={isOpen => {
          setIsEmojiPickerOpen(isOpen)
          if (!isOpen) {
            setTimeout(() => {
              setShowReactionBar(false)
            }, 100)
          }
        }}
        buttonClassName="w-6 h-6 rounded-full mx-0.5 transition-colors hover:bg-gray-100 flex items-center justify-center"
        buttonIconClassName="!size-3"
        className="w-72 !z-[99999]"
        modal={true}
        userReactions={userCustomReactions}
      />
    </>
  )

  // If iconsOnly is true, only render the reacted icons
  if (iconsOnly) {
    return (
      <>
        {groupedReactions.length > 0 && (
          <div className="flex items-center gap-1">
            <div className="flex -space-x-0.5">
              {groupedReactions.slice(0, 2).map((group, index) => (
                <motion.div
                  key={`${group.identifier}-${index}`}
                  className={cn(
                    'rounded-full flex items-center justify-center shadow-sm border cursor-pointer',
                    group.userReacted
                      ? 'border-blue-200 bg-blue-50 hover:bg-blue-100'
                      : 'border-white bg-gray-50 hover:bg-gray-100'
                  )}
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{
                    type: 'spring',
                    stiffness: 400,
                    damping: 10,
                    delay: index * 0.05,
                  }}
                  whileHover={{
                    scale: 1.3,
                    zIndex: 10,
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                    transition: { duration: 0.2 },
                  }}
                  whileTap={{ scale: 0.95 }}
                  title={`${group.count} reaction${group.count > 1 ? 's' : ''} - ${group.users.map(u => u.name).join(', ')}${group.userReacted ? ' (Click to remove your reaction)' : ' (Click to add your reaction)'}`}
                  onClick={() => {
                    // Always allow clicking to add/remove reaction
                    onReact(messageId, group.reactionType as MessageReactionType, group.emojiData)
                  }}
                >
                  {renderReactionDisplay(group, 'small')}
                </motion.div>
              ))}
            </div>

            {reactions.length > 0 && (
              <motion.div
                className="text-xs font-medium bg-gray-100 hover:bg-gray-200 px-1 py-0.5 rounded-full text-gray-600 cursor-pointer transition-colors"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.3 }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                title={`${reactions.length} total reactions`}
                onClick={() => setShowReactionList(true)}
              >
                {reactions.length}
              </motion.div>
            )}
          </div>
        )}

        <ReactionListDialog
          isOpen={showReactionList}
          onClose={() => setShowReactionList(false)}
          reactions={reactions}
          messageId={messageId}
          onRemoveReaction={onReact}
        />
      </>
    )
  }

  // If buttonOnly is true, only render the reaction button
  if (buttonOnly) {
    return (
      <div className="relative" ref={reactionBarRef}>
        {showReactionBar &&
          quickReactionFloatStyle &&
          typeof document !== 'undefined' &&
          createPortal(
            <motion.div
              className="flex items-center bg-white shadow-xl rounded-full py-1 px-2 border border-gray-100"
              style={quickReactionFloatStyle}
              initial={{ opacity: 0, y: 10, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ type: 'spring', damping: 20, stiffness: 300 }}
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
            >
              {renderQuickReactionBarBody()}
            </motion.div>,
            document.body
          )}

        {/* Reaction trigger button */}
        <motion.button
          className="flex items-center justify-center w-6 h-6 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors duration-200"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          title="React to message"
        >
          <ThumbsUp className="w-3 h-3" />
        </motion.button>
      </div>
    )
  }

  // Default: render both button and icons
  return (
    <div className="flex items-center gap-1 sm:gap-2">
      <div className="relative" ref={reactionBarRef}>
        {showReactionBar &&
          quickReactionFloatStyle &&
          typeof document !== 'undefined' &&
          createPortal(
            <motion.div
              className="flex items-center bg-white shadow-xl rounded-full py-1 px-2 border border-gray-100"
              style={quickReactionFloatStyle}
              initial={{ opacity: 0, y: 10, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ type: 'spring', damping: 20, stiffness: 300 }}
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
            >
              {renderQuickReactionBarBody()}
            </motion.div>,
            document.body
          )}

        {/* Reaction trigger button */}
        <motion.button
          className="flex items-center justify-center w-6 h-6 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors duration-200"
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          title="React to message"
        >
          <ThumbsUp className="w-3 h-3" />
        </motion.button>
      </div>

      {/* Display reaction counts - smaller for messages */}
      {groupedReactions.length > 0 && (
        <div className="flex items-center gap-1">
          <div className="flex -space-x-0.5">
            {groupedReactions.slice(0, 2).map((group, index) => (
              <motion.div
                key={`${group.identifier}-${index}`}
                className={cn(
                  'rounded-full flex items-center justify-center shadow-sm border cursor-pointer',
                  group.userReacted
                    ? 'border-blue-200 bg-blue-50 hover:bg-blue-100'
                    : 'border-white bg-gray-50 hover:bg-gray-100'
                )}
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{
                  type: 'spring',
                  stiffness: 400,
                  damping: 10,
                  delay: index * 0.05,
                }}
                whileHover={{
                  scale: 1.3,
                  zIndex: 10,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                  transition: { duration: 0.2 },
                }}
                whileTap={{ scale: 0.95 }}
                title={`${group.count} reaction${group.count > 1 ? 's' : ''} - ${group.users.map(u => u.name).join(', ')}${group.userReacted ? ' (Click to remove your reaction)' : ' (Click to add your reaction)'}`}
                onClick={() => {
                  // Always allow clicking to add/remove reaction
                  onReact(messageId, group.reactionType as MessageReactionType, group.emojiData)
                }}
              >
                {renderReactionDisplay(group, 'small')}
              </motion.div>
            ))}
          </div>

          {reactions.length > 0 && (
            <motion.div
              className="text-xs font-medium bg-gray-100 hover:bg-gray-200 px-1 py-0.5 rounded-full text-gray-600 cursor-pointer transition-colors"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              title={`${reactions.length} total reactions`}
              onClick={() => setShowReactionList(true)}
            >
              {reactions.length}
            </motion.div>
          )}
        </div>
      )}

      <ReactionListDialog
        isOpen={showReactionList}
        onClose={() => setShowReactionList(false)}
        reactions={reactions}
        messageId={messageId}
        onRemoveReaction={onReact}
      />
    </div>
  )
}

export default MessageReactions
